import { useLocation, useSearchParams } from 'react-router'
import { NewItem } from '@/types/new-item'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  addNewItem,
  addReturnedItem,
  ApiReturnedItemSubproduct,
  ExchangedItemType,
  ItemActions,
  removeNewItem,
  removeNewItems,
  removeReturnedItem,
  updateNewItemQuantity,
  updateReturnedItem
} from '@/redux/return/return-slice'
import { RefundAction } from '@itsrever/returns-api-types'
import type {
  ReturnsProductDetailsResponse,
  ReturnsProductDetailsResponseVariantInfo
} from '@itsrever/rever-api'

export function useCart() {
  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const { state } = useLocation()

  const isMisako = ecommerceGroup === 'misako'
  const isHoff = ecommerceGroup === 'hoff'

  const dispatch = useAppDispatch()
  const newItems = useAppSelector((state) => state.return.newItems)
  const returnedItems = useAppSelector((state) => state.return.returnedItems)
  const cartItems = newItems.filter(
    (newItem) => newItem.exchangeType === ExchangedItemType.FULL_CATALOG
  )

  const [searchParams] = useSearchParams()
  const activeReturnedItemId = searchParams.get('lineItemId')

  function removeCartItem(cartItem: NewItem) {
    dispatch(
      removeNewItem({
        returnedItemId: cartItem.returnedItemId,
        variantId: cartItem.productVariant.variantId
      })
    )

    const exchangeSelectionsForItem = newItems.filter(
      (item) => item.returnedItemId === cartItem.returnedItemId
    )
    const isLastExchangeSelection = exchangeSelectionsForItem.length <= 1

    const returnedItem = returnedItems.find(
      (item) => item.lineItemId === cartItem.returnedItemId
    )

    if (!returnedItem || !isLastExchangeSelection) {
      return
    }

    const isRemovingFromActiveItem =
      cartItem.returnedItemId === activeReturnedItemId

    if (!isMisako && !isHoff) {
      dispatch(
        removeReturnedItem({
          lineItemId: cartItem.returnedItemId
        })
      )
      return
    }

    if (!isRemovingFromActiveItem) {
      dispatch(
        removeReturnedItem({
          lineItemId: returnedItem.lineItemId
        })
      )
    }
  }
  function updateCartItemQuantity(cartItem: NewItem, quantity: number) {
    dispatch(
      updateNewItemQuantity({
        returnedItemId: cartItem.returnedItemId,
        variantId: cartItem.productVariant.variantId,
        quantity
      })
    )
  }
  function addItemToCart(
    returnedItemId: string,
    selectedQuantity: number,
    variant: ReturnsProductDetailsResponseVariantInfo,
    productDetails: ReturnsProductDetailsResponse
  ) {
    const existingItem = newItems.find(
      (item) =>
        item.returnedItemId === returnedItemId &&
        item.productVariant.variantId === variant?.platform_id
    )
    if (existingItem) {
      updateCartItemQuantity(
        existingItem,
        existingItem.quantity + selectedQuantity
      )
    } else {
      const newItem: NewItem = {
        exchangeType: ExchangedItemType.FULL_CATALOG,
        returnedItemId,
        quantity: selectedQuantity,
        price: parseFloat(variant?.price?.amount ?? '0'),
        currencyCode: variant?.price?.currency_code,
        signedProductRaw: productDetails.signed_product_raw ?? '',
        collectionIds: productDetails.product_info?.collection_ids ?? [],
        productVariant: {
          productName: productDetails.product_info?.name ?? '',
          variantName: variant?.name ?? '',
          productId: productDetails.product_info?.platform_id ?? '',
          variantId: variant?.platform_id ?? '',
          imageUrl: variant?.image_urls?.[0] ?? '',
          imagesUrls: variant?.image_urls ?? [],
          inventoryQuantity: variant?.inventory_quantity ?? 1
        }
      }

      dispatch(addNewItem(newItem))

      const returnedItem = returnedItems.find(
        (returnedItem) => returnedItem.lineItemId === newItem.returnedItemId
      )

      if (
        returnedItem &&
        returnedItem.item.action === RefundAction.RefundAction_TO_RETURN
      ) {
        dispatch(
          updateReturnedItem({
            ...returnedItem,
            item: {
              ...returnedItem.item,
              action: RefundAction.RefundAction_TO_EXCHANGE
            },
            action: ItemActions.ToExchangeFullCatalog
          })
        )
      }

      const returnedItemState = state?.returnedItem as ApiReturnedItemSubproduct

      if (!returnedItem && returnedItemState) {
        dispatch(
          addReturnedItem({
            ...returnedItemState,
            item: {
              ...returnedItemState.item,
              action: RefundAction.RefundAction_TO_EXCHANGE
            },
            action: ItemActions.ToExchangeFullCatalog
          })
        )
      }
    }
  }

  function clearReturnedItemCartItems(returnedItemId: string) {
    dispatch(removeNewItems({ returnedItemId }))
  }

  function getCartTotal(items: NewItem[]): number {
    return items.reduce((acc, item) => acc + item.quantity, 0)
  }

  return {
    removeCartItem,
    updateCartItemQuantity,
    addItemToCart,
    clearReturnedItemCartItems,
    cartItems,
    getCartTotal
  }
}
